/* ---------------------------------------------------------------------------
   Previewing a screen on another date
   ---------------------------------------------------------------------------
   The Dashboard's date filter shows the selected screen exactly as its display
   will look on the picked date. Nothing about the display changes for that but
   its clock: it runs on the picked date at the time of day it is right now, and
   the backend moves its "now" onto the very same moment (the `filterDate` of
   getScreenDisplayWindow), so the content handed over and every clock-driven
   element — zmanim, rolls, Shabbos windows, banners, schedule gating — describe
   one moment, as the screen itself will show it.

   A time range on that date (`filterStartTime` / `filterEndTime` — the whole
   day, "00:00" to "24:00", unless the Dashboard's time picker narrows it)
   widens the moment into a span: the backend hands over everything on air at
   any moment of it. The display keeps the current time of day while that falls
   inside the range, and otherwise runs from the range's start.

   Every reading is taken on the SCREEN's own clock (its timezone), never the
   clock of the browser doing the previewing, so an admin in another timezone
   sees the screen's day rather than their own.
   -------------------------------------------------------------------------- */

import { getZoneOffsetMs } from './display_schedule';

export interface CalendarDateParts {
  year: number;
  month: number; // 1-12
  day: number;
}

export interface ClockTimeParts {
  hour: number;
  minute: number;
  second: number;
}

export interface ZonedDateTimeParts extends CalendarDateParts, ClockTimeParts {}

export interface PreviewRange {
  from: Date;
  to: Date;
}

// Minutes in a day: a time range runs from 0 (midnight) to 1440 (the next one).
export const DAY_MINUTES = 24 * 60;

const DATE_VALUE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const TIME_VALUE_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

const DAY_END_RE = /^24:00(?::00)?$/;

const pad2 = (value: number) => String(value).padStart(2, '0');

// One formatter per timezone: building one is the slow part, and the display
// reads the screen's clock many times a second. An unknown timezone throws here
// and is never cached.
const formatters = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
};

// A "YYYY-MM-DD" value as its parts, or null unless it names a real calendar
// date. Years before 1000 are refused too: a native date input reports every
// keystroke of a year being typed ("0002-…", "0020-…"), and no one means those.
export const parseDateValue = (value: unknown): CalendarDateParts | null => {
  const match = typeof value === 'string' ? value.trim().match(DATE_VALUE_RE) : null;
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1000) return null;

  const check = new Date(Date.UTC(year, month - 1, day));
  const isRealDate =
    check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day;
  return isRealDate ? { year, month, day } : null;
};

// An "HH:mm" (or "HH:mm:ss") time of day as its parts, or null. "24:00" is the
// end of the day — the next midnight.
export const parseTimeValue = (value: unknown): ClockTimeParts | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (DAY_END_RE.test(text)) return { hour: 24, minute: 0, second: 0 };
  const match = text.match(TIME_VALUE_RE);
  if (!match) return null;
  return { hour: Number(match[1]), minute: Number(match[2]), second: Number(match[3] || 0) };
};

// Minutes past midnight (0–1440) as the "HH:mm" a time range is sent in; the
// day's end, 1440, is "24:00".
export const timeValueFromMinutes = (minutes: number): string => {
  const total = Math.min(Math.max(Math.round(Number(minutes) || 0), 0), DAY_MINUTES);
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

// The instant a screen in `timeZone` reads this date and time of day. Two passes
// so a time either side of a DST change still resolves to the instant that
// clock face actually happens at.
const wallClockToInstant = (date: CalendarDateParts, time: ClockTimeParts, timeZone: string): number => {
  const wallClock = Date.UTC(date.year, date.month - 1, date.day, time.hour, time.minute, time.second);
  const firstPass = wallClock - getZoneOffsetMs(wallClock, timeZone);
  return wallClock - getZoneOffsetMs(firstPass, timeZone);
};

// The wall-clock reading of `instant` on a screen in `timeZone`, or null when
// the instant is unreadable or the timezone unknown to this browser.
export const getZonedDateTimeParts = (
  instant: Date | number,
  timeZone: string,
): ZonedDateTimeParts | null => {
  const millis = instant instanceof Date ? instant.getTime() : Number(instant);
  if (!Number.isFinite(millis)) return null;

  try {
    const parts = getFormatter(timeZone).formatToParts(new Date(millis));

    const map: Record<string, string> = {};
    parts.forEach((part) => { map[part.type] = part.value; });

    const reading: ZonedDateTimeParts = {
      year: Number(map.year),
      month: Number(map.month),
      day: Number(map.day),
      // Older engines print midnight as hour 24 even under h23.
      hour: Number(map.hour) % 24,
      minute: Number(map.minute),
      second: Number(map.second),
    };
    return Object.values(reading).every(Number.isFinite) ? reading : null;
  } catch (error) {
    return null;
  }
};

// "YYYY-MM-DD" a screen in `timeZone` reads at `instant` — that screen's today.
export const getDateValueInTimeZone = (instant: Date | number, timeZone: string): string => {
  const parts = getZonedDateTimeParts(instant, timeZone);
  if (!parts) return '';
  return `${String(parts.year).padStart(4, '0')}-${pad2(parts.month)}-${pad2(parts.day)}`;
};

// Device-local noon of a calendar date. kosher-zmanim calendars, JewishCalendar
// and getDay() all read a JS Date's local year / month / day, and noon keeps
// clear of any DST or midnight edge, so this pins them to that very date
// whatever timezone the device runs in.
export const toLocalNoon = ({ year, month, day }: CalendarDateParts): Date => {
  const date = new Date(2000, 0, 1, 12, 0, 0, 0);
  // setFullYear, unlike the constructor, never reads years 0-99 as 19xx.
  date.setFullYear(year, month - 1, day);
  return date;
};

// The moment a screen in `timeZone` reads `dateValue` ("YYYY-MM-DD") at the
// same time of day it reads at `now`: the picked date "at this time of day",
// which is the moment a preview of that date shows. Null when the date or the
// timezone can't be read.
export const getPreviewInstant = (
  dateValue: string,
  timeZone: string,
  now: Date | number = Date.now(),
): Date | null => {
  const date = parseDateValue(dateValue);
  const nowMillis = now instanceof Date ? now.getTime() : Number(now);
  const clock = getZonedDateTimeParts(nowMillis, timeZone);
  if (!date || !clock) return null;

  // Whole seconds through the timezone conversion (offsets are whole minutes),
  // with the milliseconds put back afterwards so the clock still ticks smoothly.
  const instant = wallClockToInstant(date, clock, timeZone);
  return new Date(instant + (((nowMillis % 1000) + 1000) % 1000));
};

// A time range previewed on `dateValue`: from its start time — else the top of
// the day — to its end time — else the day's last moment, which is also what
// "24:00" reads as. Null when neither end is given, or the range ends before it
// starts. Mirrors the backend's resolveTimeRange, so the display runs across
// exactly what was fetched.
export const getPreviewRange = (
  dateValue: string,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  timeZone: string,
): PreviewRange | null => {
  const date = parseDateValue(dateValue);
  const start = parseTimeValue(startTime);
  const end = parseTimeValue(endTime);
  if (!date || (!start && !end)) return null;

  const instantAt = (time: ClockTimeParts): number => (time.hour >= 24
    ? wallClockToInstant(date, { hour: 23, minute: 59, second: 59 }, timeZone) + 999
    : wallClockToInstant(date, time, timeZone));
  const from = instantAt(start || { hour: 0, minute: 0, second: 0 });
  const to = instantAt(end || { hour: 24, minute: 0, second: 0 });
  return to < from ? null : { from: new Date(from), to: new Date(to) };
};

// The moment a preview opens on: the date at the current time of day while that
// falls inside the previewed range — so the whole day still looks live — else
// the range's start. Null when the date or the timezone can't be read.
export const getPreviewOpeningInstant = (
  dateValue: string,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  timeZone: string,
  now: Date | number = Date.now(),
): Date | null => {
  const moment = getPreviewInstant(dateValue, timeZone, now);
  const range = getPreviewRange(dateValue, startTime, endTime, timeZone);
  if (!range) return moment;
  if (!moment || moment < range.from || moment > range.to) return range.from;
  return moment;
};
